import React, { useContext, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Box,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  gray100,
  gray80,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import CustomSnackbar from '../form/CustomSnackbar';
import DeleteModal from '../modal/DeleteModal';
import ReSendInviteMailModal from '../modal/ReSendInviteMailModal';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { adminCountError, authError } from '../../../modules/error';
import { userRole, registerStatus } from '../../../constants/enum';
import {
  USER_ROLE_OPERATOR_ADMIN,
  USER_ROLE_OPERATOR,
  USER_ROLE_COMPANY_ADMIN,
  USER_ROLE_COMPANY_MEMBER,
  USER_ROLE_UNKNOWN,
  GROUP_COLUMN_LABEL,
} from '../../../constants/labels';
import CustomLoading from '../form/CustomLoading';
import { AuthContext } from '../../../contexts/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../api/firebase/config';
import NoAccountIcon from '../../assets/Account circle.svg';
import { useAccess } from '../../../contexts/index';
import { UserType } from '../../../types/user';
import { deleteUser } from '../../../api/firebase/firestore/user/delete';
import useSnackbar from '../../../hooks/useSnackbar';
import { getMonthlyUsageCountByUserIdRealTime } from '../../../api/firebase/firestore/company/read';
import { getUserMonthlyOcrLimit } from '../../../utils/userUsageLimit';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { isNotCompanyMember } from '../../../utils/permission';
import * as Sentry from '@sentry/react';

type StaffListCardProps = {
  isAdmin: boolean;
  staffList: UserType[];
  loginUser: UserType | null;
  /** グループID→グループ名のマップ（#2804。所属グループバッジ表示用） */
  groupNameMap?: Record<string, string>;
};

const styles = {
  paper: {
    padding: '20px',
    backgroundColor: white,
    border: '1px solid #E0E0E0',
    overflowY: 'auto',
    paddingBottom: '220px',
    '@media (max-width: 600px)': {
      paddingBottom: '0',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '& table': {
      minWidth: '1200px',
    },
  },
  tableHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
  },
  tableCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
  },
  nameCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '120px',
    whiteSpace: 'nowrap',
  },
  nameCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '120px',
    whiteSpace: 'nowrap',
  },
  departmentCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '100px',
    whiteSpace: 'nowrap',
  },
  departmentCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '100px',
    whiteSpace: 'nowrap',
  },
  emailCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '200px',
    whiteSpace: 'nowrap',
  },
  emailCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '200px',
    whiteSpace: 'nowrap',
  },
  roleCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '80px',
    whiteSpace: 'nowrap',
  },
  roleCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '80px',
    whiteSpace: 'nowrap',
  },
  countCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '100px',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  countCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '100px',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  actionCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '200px',
    whiteSpace: 'nowrap',
  },
  actionCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '200px',
    whiteSpace: 'nowrap',
  },
  avatar: {
    width: 32,
    height: 32,
    marginRight: '8px',
  },
  buttonHeight: {
    height: '32px',
  },
  deleteButton: {
    marginLeft: '16px',
  },
  memberText: {
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
  },
  modalCloseButton: {
    cursor: 'pointer',
    height: '32px',
    width: '32px',
  },
  modalTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 40px 0 40px',
  },
  modalTitleText: {
    fontSize: '22px',
    fontWeight: 400,
    '@media (max-width: 600px)': {
      fontSize: '20px',
    },
    '@media (max-width: 320px)': {
      fontSize: '18px',
    },
  },
  dialogContent: {
    padding: '24px 40px 0 40px',
  },
  modalContentText: {
    fontSize: '16px',
    fontWeight: 400,
    '@media (max-width: 320px)': {
      fontSize: '14px',
    },
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 40px 40px 40px',
  },
  actionButton: {
    fontSize: '16px',
    fontWeight: 700,
    width: '100%',
    '@media (max-width: 600px)': {
      fontSize: '14px',
    },
    '@media (max-width: 320px)': {
      fontSize: '12px',
    },
  },
};

const getRoleText = (staffRole: number): string => {
  if (staffRole === userRole.admin) return USER_ROLE_OPERATOR_ADMIN;
  if (staffRole === userRole.operator) return USER_ROLE_OPERATOR;
  if (staffRole === userRole.companyAdmin) return USER_ROLE_COMPANY_ADMIN;
  if (staffRole === userRole.companyMember) return USER_ROLE_COMPANY_MEMBER;
  return USER_ROLE_UNKNOWN;
};

const StaffListCard: React.FC<StaffListCardProps> = ({
  isAdmin,
  staffList,
  loginUser,
  groupNameMap = {},
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [selectedDeleteUid, setSelectedDeleteUid] = useState<string>('');
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [usageData, setUsageData] = useState<{ [uid: string]: number }>({});
  const { user } = useContext(AuthContext);
  const loginUserUid = user?.uid;
  const { allowAccess } = useAccess();
  const { companyId } = useCompanyId();

  // 利用枚数データをリアルタイム監視で取得（ocr_usage_monthlyのusage_count合計）
  React.useEffect(() => {
    if (!companyId || staffList.length === 0) {
      return;
    }

    const newUnsubscribeMap: { [uid: string]: () => void } = {};

    // 各スタッフの利用枚数をリアルタイム監視
    for (const staff of staffList) {
      const unsubscribe = getMonthlyUsageCountByUserIdRealTime(
        companyId,
        staff.uid,
        (count: number) => {
          setUsageData(prev => ({ ...prev, [staff.uid]: count }));
        },
      );
      newUnsubscribeMap[staff.uid] = unsubscribe;
    }

    // クリーンアップ関数
    return () => {
      Object.values(newUnsubscribeMap).forEach(unsubscribe => unsubscribe());
    };
  }, [staffList, companyId]);

  const handleOpen = (email: string): void => {
    setSelectedEmail(email);
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    setSelectedEmail('');
  };

  const handleDeleteUser = async (): Promise<void> => {
    setLoading(true);

    const existUser = staffList.filter(staff => staff.uid === selectedDeleteUid).length;

    if (existUser < 1) {
      setErrorText(authError.userNotFound);
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      if (!loginUserUid) throw new Error('ログインしてください。');
      const targetUser = staffList.find(staff => staff.uid === selectedDeleteUid);
      if (!targetUser) {
        return;
      }

      // 削除対象が企業管理者の場合、企業管理者数をチェック
      if (targetUser.role === userRole.companyAdmin) {
        const companyAdminCount = staffList.filter(
          staff => staff.role === userRole.companyAdmin,
        ).length;
        if (companyAdminCount <= 1) {
          // 企業管理者が1人しかいない場合は削除不可
          setErrorText(adminCountError);
          setSnackbarOpen(true);
          setLoading(false);
          return;
        }
      }

      const { register_status: userRegisterStatus } = targetUser;

      // ユーザー削除処理
      await deleteUser(selectedDeleteUid, loginUserUid);

      if (userRegisterStatus === registerStatus.emailRegistered) {
        navigate('/staff/invite/cancel');
      } else if (userRegisterStatus === registerStatus.finished) {
        navigate('/staff/delete/complete');
      } else {
        return;
      }
    } catch (error: unknown) {
      // Sentryにエラーを送信
      Sentry.captureException(error);

      let errorMessage: string;

      if (error instanceof FirebaseError) {
        errorMessage = getErrorMessageFromAuthErrorCode(error.code);
      } else {
        errorMessage = '担当者の削除に失敗しました。';
      }

      setLoading(false);
      setErrorText(errorMessage);
      setSnackbarOpen(true);
      return;
    }
  };

  const resendEmail = async (): Promise<void> => {
    setLoading(true);
    try {
      // 対象ユーザーの情報を取得
      const targetUser = staffList.find(user => user.email === selectedEmail);
      if (!targetUser) {
        throw new Error('ユーザー情報が見つかりません。');
      }

      // SendGrid招待メール送信
      const sendStaffInvitation = httpsCallable<
        { email: string; role: string; isResend?: boolean },
        { success: boolean; message: string }
      >(functions, 'sendStaffInvitation');

      // 一般メンバー以外の場合は 'admin'、それ以外は 'member'
      const role = isNotCompanyMember(targetUser.role) ? 'admin' : 'member';
      const result = await sendStaffInvitation({
        email: selectedEmail,
        role,
        isResend: true,
      });

      if (result.data.success) {
        handleClose();
        setLoading(false);
        setSnackbarOpen(true);
        setInfoText('招待メールを再送しました。');
      } else {
        throw new Error('メール送信に失敗しました。');
      }
    } catch (error) {
      setLoading(false);
      setErrorText('メールの送信に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      <CustomLoading loading={loading} />
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.nameCellHeader}>担当者名</TableCell>
              <TableCell sx={styles.departmentCellHeader}>部署</TableCell>
              <TableCell sx={styles.roleCellHeader}>権限</TableCell>
              <TableCell sx={styles.roleCellHeader}>{GROUP_COLUMN_LABEL}</TableCell>
              <TableCell sx={styles.emailCellHeader}>メールアドレス</TableCell>
              <TableCell sx={styles.countCellHeader}>利用枚数</TableCell>
              <TableCell sx={styles.actionCellHeader}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staffList.map(staff => (
              <TableRow key={staff.uid}>
                <TableCell sx={styles.nameCell}>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={styles.avatar} src={staff.icon_url || NoAccountIcon} />
                    <Typography sx={styles.tableCell}>{`${staff.name ?? ''}`}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={styles.departmentCell}>{staff.department || '-'}</TableCell>
                <TableCell sx={styles.roleCell}>
                  {getRoleText(staff.role ?? userRole.companyMember)}
                </TableCell>
                <TableCell sx={styles.roleCell}>
                  {(staff.group_ids ?? []).filter(id => groupNameMap[id]).length === 0 ? (
                    <Typography sx={{ color: gray80, fontSize: '14px' }}>-</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(staff.group_ids ?? [])
                        .filter(id => groupNameMap[id])
                        .map(id => (
                          <Chip key={id} label={groupNameMap[id]} size="small" variant="outlined" />
                        ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={styles.emailCell}>{staff.email}</TableCell>
                <TableCell sx={styles.countCell}>
                  {usageData[staff.uid] !== undefined
                    ? `${usageData[staff.uid]} / ${getUserMonthlyOcrLimit(staff) ?? '-'}`
                    : '読み込み中...'}
                </TableCell>
                <TableCell align="right" sx={styles.actionCell}>
                  {!isAdmin && loginUser?.uid !== staff.uid ? (
                    <Typography sx={styles.memberText}>編集権限がありません</Typography>
                  ) : staff.register_status === registerStatus.finished ? (
                    <Button
                      sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                      onClick={(): void => {
                        allowAccess('/staff/edit');
                        navigate('/staff/edit', { state: { id: staff.uid, isAdmin } });
                      }}
                    >
                      編集
                    </Button>
                  ) : (
                    <Button
                      sx={[secondaryButtonBackgroundColor, styles.buttonHeight]}
                      onClick={(): void => handleOpen(staff.email)}
                    >
                      招待メールを再送
                    </Button>
                  )}
                  {isAdmin ? (
                    <Button
                      onClick={(): void => {
                        setSelectedDeleteUid(staff.uid);
                        setDeleteModalOpen(true);
                      }}
                      sx={[tertiaryButtonBackgroundColor, styles.deleteButton, styles.buttonHeight]}
                    >
                      削除
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReSendInviteMailModal
        open={open}
        onClose={(): void => handleClose()}
        onSend={resendEmail}
        title={selectedEmail}
      />
      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeleteUser}
        title="担当者"
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </Paper>
  );
};

export default StaffListCard;
